import type { CurrencyCode } from '@noths/polaris-client-localisation';

import {
  ADD_ITEM,
  ADD_MEMBERSHIP_ITEM,
  REQUEST_IMAGE_UPLOAD_URLS,
  UPDATE_ITEM,
} from './graphql/mutations';
import type {
  BasketAPIAddItemMutationResponseBody,
  BasketAPIAddMembershipItemMutationResponseBody,
  BasketAPIRequestImageUploadUrlsResponseBody,
  BasketAPIUpdateItemMutationResponseBody,
  GraphQLError,
} from './types/BasketAPIResponseBody';
import type { ProductOptionInputWithoutLabel } from './types/graphql';
import { graphqlRequest } from './utils/graphqlRequest';

export interface AddItemMutationVariables {
  code: string;
  currencyCode: CurrencyCode;
  options: ProductOptionInputWithoutLabel;
  quantity: number;
}

export class BasketAPIError extends Error {
  public code: string | undefined;
  public basketApiMessage: string | undefined;
  public errors: GraphQLError[];
  constructor(
    message: string,
    code?: string,
    basketApiMessage?: string,
    errors: GraphQLError[] = [],
  ) {
    super(message);
    this.name = 'BasketAPIError';
    this.code = code;
    this.basketApiMessage = basketApiMessage;
    this.errors = errors;
  }
}

export const addItem = async (
  productCode: number,
  quantity: number,
  currencyCode: CurrencyCode = 'GBP',
  options: ProductOptionInputWithoutLabel,
) => {
  const response = await graphqlRequest<
    BasketAPIAddItemMutationResponseBody,
    AddItemMutationVariables
  >(ADD_ITEM, {
    code: String(productCode),
    currencyCode,
    quantity,
    options,
  });

  if (!response.data.addItem.success) {
    const { errors } = response.data.addItem;
    const firstError = errors[0];

    throw new BasketAPIError(
      firstError?.message ?? 'Unknown error',
      firstError?.code,
      firstError?.message,
      errors,
    );
  }

  return response.data.addItem;
};

interface UpdateItemMutationVariables {
  currencyCode: CurrencyCode;
  id: string;
  options: ProductOptionInputWithoutLabel;
  quantity: number;
}

export const updateItem = async (
  id: string,
  quantity: number,
  currencyCode: CurrencyCode = 'GBP',
  options: ProductOptionInputWithoutLabel,
) => {
  const response = await graphqlRequest<
    BasketAPIUpdateItemMutationResponseBody,
    UpdateItemMutationVariables
  >(UPDATE_ITEM, {
    id,
    quantity,
    currencyCode,
    options,
  });

  if (!response.data.updateItem.success) {
    const { errors } = response.data.updateItem;
    const firstError = errors[0];

    throw new BasketAPIError(
      firstError?.message ?? 'Unknown error',
      firstError?.code,
      firstError?.message,
      errors,
    );
  }

  return response.data.updateItem;
};

export interface RequestImageUploadUrlsVariables {
  count?: number;
  optionId?: number | string;
  productCode?: number | string;
}

export const requestImageUploadUrls = async ({
  count,
  optionId,
  productCode,
}: RequestImageUploadUrlsVariables) => {
  const response = await graphqlRequest<
    BasketAPIRequestImageUploadUrlsResponseBody,
    {
      count?: number;
      optionId?: string;
      productCode?: string;
    }
  >(REQUEST_IMAGE_UPLOAD_URLS, {
    count,
    optionId: optionId != null ? String(optionId) : undefined,
    productCode: productCode != null ? String(productCode) : undefined,
  });

  return response.data.requestImageUploadUrls.slots;
};

export const addMembershipItem = async (currency: CurrencyCode) => {
  const response = await graphqlRequest<
    BasketAPIAddMembershipItemMutationResponseBody,
    { currency: CurrencyCode }
  >(ADD_MEMBERSHIP_ITEM, {
    currency,
  });

  if (!response.data.addMembershipItem.success) {
    const errorMessages = response.data.addMembershipItem.errors.map((error) => error.message);

    throw new Error(
      `the addMembershipItem basket-api mutation failed: ${errorMessages.join(', ')}`,
    );
  }

  return response.data.addMembershipItem;
};
